import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Modal, Button, Spinner } from '@belong/ui';
import {
  HomeDetailInfoModel,
  MonthSummaryInfoModel,
  BillingInfoModel,
  GroupedBalancesInfoModel,
} from 'api/models.v2';
import {
  startOfMonth,
  endOfMonth,
  startOfDay,
  format,
  subMonths,
  isAfter,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { MonthSummary } from 'pages/Accounts/Components/month-summary/month-summary';
import { getMonthName } from 'pages/Accounts/utils/get-month-name';
import { QuestionsOrConcernsModalFooter } from 'post-inspection/components/question-or-concern-modal-footer/question-or-concern-modal-footer';
import { fetchUsersBalancesPdf } from 'store/redux/accounts/actions';
import {
  selectAccountPropertyByUnitId,
  selectAccountUnitByUnitId,
} from 'store/redux/homeowner-accounts/selectors';
import { useAppDispatch } from 'store/redux/use-app-dispatch';
import { formatString } from 'strings';
import { addressAndUnit } from 'utils/formatAddress';
import { EARNINGS_STRINGS } from '../earnings.strings';
import { Downloader } from './downloader';

type PastMonthModalProps = {
  address?: string;
  canDownload?: boolean;
  onHide: () => void;
  show?: boolean;
  homeDetail: HomeDetailInfoModel & { monthSummaryInfo: MonthSummaryInfoModel };
  handleFinancingModal?: (
    id: string | number,
    balance: GroupedBalancesInfoModel,
    home: string,
  ) => void;
  handleShowFinanceSuccessModal?: () => void;
  handleShowBalanceDetailsModal?: (
    balance: GroupedBalancesInfoModel,
    home: string,
    billingInfo?: BillingInfoModel,
  ) => void;
};

export function PastMonthModal({
  onHide,
  canDownload = true,
  // Defaulted so `Modal`, whose `isOpen` is required, gets a boolean. `undefined`
  // was already treated as closed, so this is the same behaviour written down.
  show = false,
  homeDetail,
  handleFinancingModal,
  handleShowFinanceSuccessModal,
  handleShowBalanceDetailsModal,
  address = '',
}: PastMonthModalProps) {
  // Typed rather than reseeded: `useState(null)` gave the state the type `null`,
  // so it could never hold the object URL `downloadPDF` puts in it.
  const [blob, setBlob] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // `useAppDispatch` rather than `useDispatch`: `clientMiddleware` resolves a
  // `promise` action to the client's result, which is what `downloadPDF` awaits.
  const dispatch = useAppDispatch();

  const property = useSelector(
    selectAccountPropertyByUnitId(homeDetail?.homeId),
  );
  const unit = useSelector(selectAccountUnitByUnitId(homeDetail?.homeId));
  const homeAddress =
    address ||
    formatString(
      addressAndUnit(property?.address, unit?.basicInfo!.unitNumber),
    );
  // `basicInfo` is optional on the unit model but every read here was already
  // unconditional, and `getMonthName` returns `undefined` only for a month
  // outside 1–12. Both assertions keep the existing throw rather than turning it
  // into a silently-skipped branch.
  const monthName = getMonthName(homeDetail.monthSummaryInfo.month)!;
  const pdfAddress =
    address ||
    addressAndUnit(property?.address, unit?.basicInfo!.unitNumber).replace(
      /(<([^>]+)>)/gi,
      '',
    );

  function getShouldShowPDFButton(year: number, month: number) {
    const date = subMonths(
      utcToZonedTime(new Date(), 'America/Los_Angeles'),
      1,
    );

    const otherDate = utcToZonedTime(new Date(), 'America/Los_Angeles');
    otherDate.setDate(7);
    otherDate.setMonth(month - 1);
    otherDate.setFullYear(year);

    return isAfter(date, startOfDay(otherDate));
  }

  function handleClose() {
    if (onHide) onHide();
  }

  async function downloadPDF() {
    try {
      setLoading(true);

      const from = startOfMonth(
        new Date().setMonth(homeDetail.monthSummaryInfo.month - 1),
      );
      const until = endOfMonth(from);

      const pdfBlob = await dispatch(
        fetchUsersBalancesPdf({
          homeUniqueId: homeDetail.homeId,
          from: `${homeDetail.monthSummaryInfo.year}-${format(from, 'MM-dd')}T00:00:00.000Z`,
          until: `${homeDetail.monthSummaryInfo.year}-${format(until, 'MM-dd')}T23:59:59.999Z`,
          groupBalanceType: address ? 'Flat' : 'ExpensesAndRevenues',
        }),
      );
      if (pdfBlob) {
        setBlob(pdfBlob);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      isOpen={show}
      onDismiss={handleClose}
      name="past-months-modal"
      overlayClassName="z-toast"
      childrenClassName="md:[&>div]:border-gray"
    >
      {loading && <Spinner />}
      <div className="p-xs">
        <Text variant="h2" fontWeight="semibold" className="mt-xl text-center">
          {EARNINGS_STRINGS['modal.past_month.title']}
        </Text>
        <div className="mx-md my-lg">
          <div className="mb-md flex justify-between">
            <Text className="mb-xs text-h3-sm">{homeAddress}</Text>
            <Text className="mb-xs text-right text-h3-sm">{`${monthName.slice(0, 3)} ${
              homeDetail.monthSummaryInfo.year
            }`}</Text>
          </div>
          <MonthSummary
            address={address}
            homeDetail={homeDetail}
            handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
            handleFinancingModal={handleFinancingModal}
            handleShowFinanceSuccessModal={handleShowFinanceSuccessModal}
            onlyHome
            noBorder
            hideSplitIt
          />
        </div>
      </div>

      <QuestionsOrConcernsModalFooter className="md:border-gray md:py-xl">
        {getShouldShowPDFButton(
          homeDetail.monthSummaryInfo.year,
          homeDetail.monthSummaryInfo.month,
        ) && (
          <>
            {canDownload && (
              <Button
                variant="text"
                onClick={downloadPDF}
                buttonType="secondary"
              >
                {EARNINGS_STRINGS['monthly_statement.download_pdf']}
              </Button>
            )}
          </>
        )}
      </QuestionsOrConcernsModalFooter>
      {blob && (
        <Downloader
          blob={blob}
          filename={`${monthName.slice(0, 3)} ${homeDetail.monthSummaryInfo.year} - ${pdfAddress}`}
          onDownload={() => setBlob(null)}
        />
      )}
    </Modal>
  );
}
